import { useState, useEffect, useRef } from 'react';
import { Search, X } from 'lucide-react';
import { searchBlocks, getImageUrl } from '@ego/api';
import { relativeTime } from '@ego/domain';
import useFocusTrap from '../../hooks/useFocusTrap';
import BlockCard from '../BlockCard/BlockCard';
import styles from './SearchOverlay.module.css';

/**
 * Full-text search over the owner's blocks — mirrors iOS SearchScreen:
 * autofocused field, 350ms debounce, queries under 2 characters show the
 * idle state, results in a block grid. Card click navigates to the block
 * detail page and closes the overlay.
 */
export default function SearchOverlay({ ownerHandle, onClose }) {
  const dialogRef = useRef(null);
  useFocusTrap(dialogRef);

  const [query, setQuery] = useState('');
  // null until a query has produced a response (idle / still debouncing).
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) {
      setResults(null);
      setSearching(false);
      setError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setSearching(true);
      searchBlocks(text)
        .then(rows => {
          if (cancelled) return;
          setResults(rows.map(block => ({
            ...block,
            imageUrl: getImageUrl(block.imagePath),
            updated: relativeTime(block.updatedAt),
          })));
          setError(null);
        })
        .catch(err => {
          if (cancelled) return;
          setError(err.message || 'Search failed');
          setResults(null);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // Escape closes the overlay no matter where focus sits. Capture-phase on
  // window so it runs (and stops) before BlockDetailPage's own window
  // listener — otherwise one Escape would close both the overlay and the
  // block detail underneath (navigating away from the block).
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', handleKey, true);
    return () => window.removeEventListener('keydown', handleKey, true);
  }, [onClose]);

  // Result cards are Links (BlockCard); close the overlay once one is followed.
  const handleResultsClick = (e) => {
    if (e.target.closest('a')) onClose();
  };

  let body = null;
  if (error) {
    body = <p className={styles.stateMessage}>{error}</p>;
  } else if (results) {
    body = results.length === 0 ? (
      <p className={styles.stateMessage}>No blocks found</p>
    ) : (
      <div className={styles.grid} onClick={handleResultsClick}>
        {results.map((block, i) => (
          <BlockCard
            key={block.id}
            block={block}
            channelSlug={block.channelSlug}
            channelHandle={ownerHandle}
            index={i}
          />
        ))}
      </div>
    );
  } else if (searching) {
    body = <p className={styles.stateMessage}>Searching…</p>;
  }

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <div className={styles.panel} ref={dialogRef} role="dialog" aria-modal="true" aria-label="Search blocks">
        <div className={styles.header}>
          <div className={styles.field}>
            <Search size={15} className={styles.fieldIcon} aria-hidden="true" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className={styles.fieldInput}
              placeholder="Search blocks"
              aria-label="Search blocks"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck="false"
            />
            {query && (
              <button
                type="button"
                className={styles.clearBtn}
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close search">
            <X size={16} />
          </button>
        </div>
        <div className={styles.results}>
          {body}
        </div>
      </div>
    </div>
  );
}
